import { FullStory, init as initFullStory } from '@fullstory/browser';
import { fullStoryIntegration } from '@sentry/fullstory';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assert } from 'ts-essentials';

import { areAnalyticsEnabledInEnv } from '../areAnalyticsEnabledInEnv';
import useQueueUntilReady from '../utils/useQueueUntilReady';
import type { Integration, UserData } from './types';

const fullStoryUrls = {
  EU: {
    host: 'eu1.fullstory.com',
    script: 'edge.eu1.fullstory.com/s/fs.js',
  },
  default: {
    host: 'fullstory.com',
    script: 'edge.fullstory.com/s/fs.js',
  },
};

export interface FullStoryProps {
  region: 'EU' | 'default';
  /**
   * Reference to the Sentry SDK. Only pass when Sentry has been initialized.
   */
  Sentry:
    | false
    | {
        addIntegration: (integration: ReturnType<typeof fullStoryIntegration>) => void;
      };
}

const useFullStoryIntegration = ({ region, Sentry }: FullStoryProps): Integration => {
  const [isInitialized, setIsInitialized] = useState(false);
  const initializationState = useRef<'idle' | 'loading' | 'done'>('idle');
  const sentryIntegrationAddedRef = useRef(false);

  useEffect(() => {
    if (!sentryIntegrationAddedRef.current && isInitialized && Sentry) {
      assert(process.env.SENTRY_ORG_SLUG, 'Unable to find Sentry organisation slug.');

      sentryIntegrationAddedRef.current = true;
      Sentry.addIntegration(
        fullStoryIntegration(process.env.SENTRY_ORG_SLUG, { client: FullStory }),
      );
    }
  }, [isInitialized, Sentry]);

  const enabled = isFullStoryEnabled();

  const trackEvent = useCallback((eventName: string, props: Record<string, unknown>) => {
    if (!isFullStoryEnabled()) return;
    FullStory('trackEvent', { name: eventName, properties: props ?? {} });
  }, []);
  const initAwareTrackEvent = useQueueUntilReady(trackEvent, isInitialized);

  const identifyUser = useCallback(({ commonData: { id, fullName, ...commonData } }: UserData) => {
    if (!isFullStoryEnabled()) return;
    FullStory('setIdentity', {
      uid: id,
      properties: { ...commonData, displayName: fullName },
    });
  }, []);
  const initAwareIdentifyUser = useQueueUntilReady(identifyUser, isInitialized);

  const resetUserIdentity = useCallback(() => {
    if (!isFullStoryEnabled()) return;
    FullStory('setIdentity', { anonymous: true });
  }, []);

  const initialize = useCallback(() => {
    if (!isFullStoryEnabled() || !process.env.FULLSTORY_ORG_ID) return;

    if (initializationState.current !== 'idle') return;
    initializationState.current = 'loading';

    const orgId = process.env.FULLSTORY_ORG_ID;

    return new Promise<void>((resolve) => {
      initFullStory(
        {
          host: fullStoryUrls[region].host,
          script: fullStoryUrls[region].script,
          orgId,
        },
        () => {
          initializationState.current = 'done';
          setIsInitialized(true);
          resolve();
        },
      );
    });
  }, [region]);

  return useMemo(
    () => ({
      name: 'fullStory',
      enabled,
      trackEvent: initAwareTrackEvent,
      initialize,
      identifyUser: initAwareIdentifyUser,
      resetUserIdentity,
    }),
    [enabled, initAwareTrackEvent, initialize, initAwareIdentifyUser, resetUserIdentity],
  );
};

const isFullStoryEnabled = () =>
  areAnalyticsEnabledInEnv() &&
  Boolean(process.env.FULLSTORY_ORG_ID) &&
  process.env.REACT_APP_CYPRESS !== 'true';

export default useFullStoryIntegration;
